import { YES_NO } from "../assessment-constant";
import { FreeTextQuestionDef, FreeTextScreenerDef } from "../assessment-types";
import {
  ANNUAL_WELLNESS_GROUPS as GROUPS,
  ANNUAL_WELLNESS_HIDDEN_QUESTIONS,
  ASSISTIVE_DEVICE_OPTIONS,
  COOKING_APPLIANCE_OPTIONS,
  DEPRESSION_OPTIONS,
  FALL_CIRCUMSTANCE_OPTIONS,
  HEALTH_MAINTENANCE_ITEMS,
  HEARING_OPTIONS,
  HEARING_QUESTIONS,
  HEATING_OPTIONS,
  LIVING_SITUATION_OPTIONS,
  MEDICATION_ADMINISTERED_BY_OPTIONS,
  MEDICATION_TRACKING_OPTIONS,
  ROUTINE_TASKS,
  SPECIFY_DETAILS_PLACEHOLDER,
  VISIT_TYPE_OPTIONS,
} from "./annual-wellness-option-constant";

type Group = (typeof GROUPS)[keyof typeof GROUPS];

const base = (
  questionId: number,
  questionText: string,
  group: Group | undefined,
  kind: FreeTextQuestionDef["kind"]
): FreeTextQuestionDef => ({
  questionId,
  questionText,
  group,
  kind,
  audience: "PATIENT",
  placeholder: SPECIFY_DETAILS_PLACEHOLDER,
});

const text = (
  id: number,
  questionText: string,
  group: Group,
  placeholder = SPECIFY_DETAILS_PLACEHOLDER
): FreeTextQuestionDef => ({
  ...base(id, questionText, group, "text"),
  placeholder,
});

const multilineText = (
  id: number,
  questionText: string,
  group: Group,
  placeholder = SPECIFY_DETAILS_PLACEHOLDER
): FreeTextQuestionDef => ({
  ...text(id, questionText, group, placeholder),
  isMultiline: true,
});

const yesNo = (
  id: number,
  questionText: string,
  group: Group,
  placeholder = SPECIFY_DETAILS_PLACEHOLDER
): FreeTextQuestionDef => ({
  ...base(id, questionText, group, "yesNo"),
  options: YES_NO,
  placeholder,
});

const single = (
  id: number,
  questionText: string,
  group: Group | undefined,
  options: string[]
): FreeTextQuestionDef => ({
  ...base(id, questionText, group, "single"),
  options,
});

const multi = (
  id: number,
  questionText: string,
  group: Group,
  options: string[]
): FreeTextQuestionDef => ({
  ...base(id, questionText, group, "multi"),
  options,
});

// Medicare Annual Wellness Visit (ANNUAL_WELLNESS), matched to the API's
// questions by their text and laid out in the visit's section order.
export const ANNUAL_WELLNESS_SCREENER: FreeTextScreenerDef = {
  hiddenQuestions: ANNUAL_WELLNESS_HIDDEN_QUESTIONS,
  questions: [
    single(1, "Visit type", undefined, VISIT_TYPE_OPTIONS),
    single(
      2,
      "Little interest or pleasure in doing things",
      GROUPS.depression,
      DEPRESSION_OPTIONS
    ),
    single(
      3,
      "Feeling down, depressed, or hopeless",
      GROUPS.depression,
      DEPRESSION_OPTIONS
    ),
    {
      ...yesNo(
        4,
        "Are you afraid of falling?",
        GROUPS.fallRisk,
        SPECIFY_DETAILS_PLACEHOLDER
      ),
    },
    yesNo(5, "Have you fallen in the past year?", GROUPS.fallRisk),
    {
      ...multi(
        6,
        "If yes, select the circumstances surrounding the fall",
        GROUPS.fallRisk,
        FALL_CIRCUMSTANCE_OPTIONS
      ),
    },
    ...HEARING_QUESTIONS.map((question, index) =>
      single(7 + index, question, GROUPS.hearing, HEARING_OPTIONS)
    ),
    multilineText(
      18,
      "List any current medical problems or conditions (one per line)",
      GROUPS.medicalProblems,
      SPECIFY_DETAILS_PLACEHOLDER
    ),
    {
      ...base(26, "Last Dilated Retinal Eye Exam", GROUPS.pastHistory, "date"),
      dateRule: "notFuture",
    },
    text(27, "Last Dilated Retinal Eye Exam provider", GROUPS.pastHistory),
    multilineText(
      28,
      "Physicians/practitioners you currently see: name / specialty (one per line)",
      GROUPS.pastHistory
    ),
    single(
      34,
      "Type of living situation",
      GROUPS.homeSafety,
      LIVING_SITUATION_OPTIONS
    ),
    yesNo(35, "Are there stairs?", GROUPS.homeSafety),
    text(36, "Who lives in the home with you?", GROUPS.homeSafety),
    multi(
      37,
      "What do you use to heat/cool your home?",
      GROUPS.homeSafety,
      HEATING_OPTIONS
    ),
    yesNo(
      38,
      "Are these units serviced on a regular basis?",
      GROUPS.homeSafety
    ),
    yesNo(
      39,
      "Do you have working smoke alarms and carbon monoxide detectors in your home?",
      GROUPS.homeSafety
    ),
    multi(
      40,
      "What home appliances are used to cook in your home?",
      GROUPS.homeSafety,
      COOKING_APPLIANCE_OPTIONS
    ),
    text(
      41,
      "Currently drives? Any recent accidents or episodes of becoming lost/disoriented in the past year (explain, or leave blank for no)",
      GROUPS.homeSafety,
      SPECIFY_DETAILS_PLACEHOLDER
    ),
    {
      ...multi(
        43,
        "How do you keep track of your daily medications?",
        GROUPS.homeSafety,
        MEDICATION_TRACKING_OPTIONS
      ),
      exclusiveOption: MEDICATION_TRACKING_OPTIONS[0],
    },
    single(
      44,
      "Who administers your medication?",
      GROUPS.homeSafety,
      MEDICATION_ADMINISTERED_BY_OPTIONS
    ),
    multi(
      45,
      "Are any assistive devices used in your home?",
      GROUPS.homeSafety,
      ASSISTIVE_DEVICE_OPTIONS
    ),
    ...ROUTINE_TASKS.map((task, index) =>
      yesNo(46 + index, task, GROUPS.socialHistory, SPECIFY_DETAILS_PLACEHOLDER)
    ),
    ...HEALTH_MAINTENANCE_ITEMS.map((item, index) =>
      text(
        58 + index,
        item,
        GROUPS.healthMaintenance,
        SPECIFY_DETAILS_PLACEHOLDER
      )
    ),
    yesNo(
      80,
      "Do you have an Advance Directive (living will)?",
      GROUPS.advanceDirective
    ),
    multilineText(81, "Advance Directive notes", GROUPS.advanceDirective),
  ],
};
